import { StyleSheet } from 'react-native';

// Respiro entre o player de "Mais relevantes" e o carrossel. Exportado porque a
// seção precisa somá-lo à altura medida do slot para informar à tela QUANTO
// espaço está devolvendo ao desmontar (a compensação de rolagem depende do
// número exato — ver `handleReleaseVideoSlot` na CommunityScreen).
export const PLAYER_SLOT_MARGIN_BOTTOM = 12;

// Estilos do módulo — auto-contidos (apenas literais), cores dinâmicas vêm via props.
export const styles = StyleSheet.create({
  // ---- Seções ----
  section: { paddingTop: 18, paddingBottom: 6 },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  sectionTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionTitle: { fontSize: 18, fontWeight: '700' },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: 'rgba(91,143,249,0.15)',
  },
  addButtonText: { fontSize: 13, fontWeight: '600', color: '#5B8FF9' },
  hScroll: { paddingHorizontal: 16, gap: 12 },
  loader: { paddingVertical: 24 },
  emptyText: { paddingHorizontal: 16, fontSize: 13, color: '#A0A3B1' },

  // ---- VideoCard ("Mais relevantes") ----
  card: { width: 280 },
  thumbWrap: {
    width: '100%',
    height: 158,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#1E2029',
  },
  thumb: { width: '100%', height: '100%' },
  durationPill: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.8)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: { fontSize: 11, fontWeight: '600', color: '#FFFFFF' },
  cardBody: { flexDirection: 'row', paddingTop: 10, gap: 8 },
  avatar: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#252832' },
  cardInfo: { flex: 1 },
  cardTitle: { fontSize: 14, fontWeight: '500', lineHeight: 18, marginBottom: 4 },
  cardMeta: { fontSize: 12 },
  cardActions: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 },
  likeBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  likeIcon: { fontSize: 14 },
  likeCount: { fontSize: 12, fontWeight: '600' },

  // ---- ShortCard ("Atualizações") ----
  shortCard: {
    width: 150,
    height: 270,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#1E2029',
  },
  shortThumb: { width: '100%', height: '100%' },
  shortOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 10,
    paddingTop: 36,
  },
  shortTitle: { fontSize: 13, fontWeight: '600', color: '#FFFFFF', marginBottom: 4 },
  shortMeta: { fontSize: 11, color: 'rgba(255,255,255,0.75)' },

  // Espaço reservado do player de "Mais relevantes". Largura TOTAL (sem padding
  // lateral) e 16:9, porque o FeedVideoOverlay que se ancora aqui é renderizado
  // em `width` de janela × `width*9/16` — qualquer recuo aqui desalinharia os dois.
  //
  // `borderRadius` espelha o do player (`styles.player` em FeedVideoOverlay.js).
  // Sem isso, a área entre o arco do player e o canto reto do slot mostrava preto
  // quadrado por baixo — os cantos "vazando" que não existem no feed, onde quem
  // recorta é o card do post. Valor duplicado de propósito: este módulo de estilos
  // é auto-contido (só literais); ao mexer no raio de lá, mexer aqui também.
  playerSlot: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000000',
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: PLAYER_SLOT_MARGIN_BOTTOM,
  },

  // ---- Composer (modal de postar) ----
  // Backdrop e folha são IRMÃOS, não pai/filho: o escurecimento entra por opacidade
  // (fade) enquanto a folha sobe (translateY). Enquanto o backdrop era o container,
  // ele subia junto com a folha — a tela escurecia de baixo para cima, em vez de
  // simplesmente escurecer.
  modalRoot: { flex: 1, justifyContent: 'flex-end' },
  modalBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.6)' },
  modalSheet: {
    backgroundColor: '#151821',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: 32,
    gap: 14,
  },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#E8EAED' },
  input: {
    backgroundColor: '#1E2029',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: '#E8EAED',
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#2A2D3A',
  },
  preview: { flexDirection: 'row', gap: 12, alignItems: 'center' },
  previewThumb: { width: 120, height: 68, borderRadius: 8, backgroundColor: '#1E2029' },
  previewTitle: { flex: 1, fontSize: 14, color: '#E8EAED', fontWeight: '500' },
  errorText: { fontSize: 13, color: '#FF6B6B' },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 4 },
  btn: { paddingHorizontal: 18, paddingVertical: 10, borderRadius: 12 },
  btnGhostText: { fontSize: 14, fontWeight: '600', color: '#A0A3B1' },
  btnPrimary: { backgroundColor: '#5B8FF9' },
  btnPrimaryText: { fontSize: 14, fontWeight: '700', color: '#FFFFFF' },
  btnDisabled: { opacity: 0.5 },

  // ---- Composer: escolha de fonte (YouTube | Galeria) ----
  sourceTabs: { flexDirection: 'row', gap: 8 },
  sourceTab: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#1E2029',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#2A2D3A',
  },
  sourceTabActive: { backgroundColor: 'rgba(91,143,249,0.18)', borderColor: '#5B8FF9' },
  sourceTabText: { fontSize: 13, fontWeight: '600', color: '#A0A3B1' },
  sourceTabTextActive: { color: '#5B8FF9' },
  galleryBtn: {
    backgroundColor: '#1E2029',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#2A2D3A',
    borderStyle: 'dashed',
  },
  galleryBtnText: { fontSize: 14, color: '#A0A3B1', fontWeight: '600' },
  galleryPicked: { fontSize: 13, color: '#51CF66', fontWeight: '600' },
  hint: { fontSize: 12, color: '#6B6E7B' },

  // ---- Seletor de área (etiqueta do conteúdo) ----
  fieldLabel: { fontSize: 13, fontWeight: '700', color: '#E8EAED', marginBottom: 2 },
  fieldHint: { fontSize: 11, color: '#6B6E7B', marginBottom: 8 },
  areaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  areaChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2A2D3A',
    backgroundColor: '#1E2029',
  },
  areaChipActive: { borderColor: '#5B8FF9', backgroundColor: 'rgba(91,143,249,0.16)' },
  areaChipText: { fontSize: 12, fontWeight: '600', color: '#A0A3B1' },
  areaChipTextActive: { color: '#8FB4F9' },

  // ---- Placeholder de miniatura (upload sem thumbnail no MVP) ----
  thumbPlaceholder: { justifyContent: 'center', alignItems: 'center', backgroundColor: '#252832' },
  thumbPlaceholderIcon: { fontSize: 34, color: 'rgba(255,255,255,0.85)' },

  // ---- Player full-screen estilo reel (vertical, paginado) ----
  reelBackdrop: { flex: 1, backgroundColor: '#000000' },
  reelList: { flex: 1 },
  reelPage: { width: '100%', backgroundColor: '#000000', justifyContent: 'center', alignItems: 'center' },
  reelVideo: { width: '100%', height: '100%' },
  playerClose: {
    position: 'absolute',
    top: 48,
    right: 20,
    zIndex: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  playerCloseText: { color: '#FFFFFF', fontSize: 20, fontWeight: '700' },
  playerError: { color: '#FF6B6B', fontSize: 14, paddingHorizontal: 24, textAlign: 'center' },

  // ---- Report (denúncia) ----
  reportBtn: { paddingHorizontal: 4 },
  reportIcon: { fontSize: 13, color: '#6B6E7B' },
});
